/**
 * Assistant model-selection hooks (AC-1616, epic AC-1609).
 *
 * Read + write hooks backing the model selector added to `AssistantChat`:
 *
 *   - `useAssistantModels()`            -> GET  /api/v1/assistant/models
 *   - `useAssistantModelPreference()`   -> GET  /api/v1/assistant/model-preference
 *   - `useSetAssistantModelPreference()`-> PUT  /api/v1/assistant/model-preference
 *
 * ## Cache keys
 *
 * Both reads are user-scoped (auth-derived), so neither threads an ID:
 *
 *   - `queryKeys.assistantModels()`           -> ['assistantModels']
 *   - `queryKeys.assistantModelPreference()`  -> ['assistantModelPreference']
 *
 * ## Invalidation contract
 *
 * `useSetAssistantModelPreference` is pessimistic — the server's response is
 * the truth (it rejects a non-allow-listed model with a 400). On success it
 * invalidates `queryKeys.assistantModelPreference()` so every subscriber (the
 * `AssistantChat` header picker) re-reads the persisted choice. It does NOT
 * invalidate `assistantModels()` — picking a model does not change the
 * allow-list. On failure it surfaces `mutation.error` and invalidates nothing.
 *
 * Structure mirrors `useApiKeys` (read) and `useApiKeyMutations` (write).
 */

import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';

import apiService from '../../services/api';
import {
  AssistantModelsResponse,
  AssistantModelPreference,
} from '../../types';
import { queryKeys } from './queryKeys';

export type UseAssistantModelsOptions = Omit<
  UseQueryOptions<
    AssistantModelsResponse,
    Error,
    AssistantModelsResponse,
    ReturnType<typeof queryKeys.assistantModels>
  >,
  'queryKey' | 'queryFn'
>;

/**
 * `useAssistantModels()` — reads the key-gated assistant allow-list
 * (`{ models, default }`). Cached under `queryKeys.assistantModels()`.
 */
export function useAssistantModels(
  options?: UseAssistantModelsOptions,
): UseQueryResult<AssistantModelsResponse, Error> {
  return useQuery<
    AssistantModelsResponse,
    Error,
    AssistantModelsResponse,
    ReturnType<typeof queryKeys.assistantModels>
  >({
    queryKey: queryKeys.assistantModels(),
    queryFn: () => apiService.getAssistantModels(),
    ...options,
  });
}

export type UseAssistantModelPreferenceOptions = Omit<
  UseQueryOptions<
    AssistantModelPreference,
    Error,
    AssistantModelPreference,
    ReturnType<typeof queryKeys.assistantModelPreference>
  >,
  'queryKey' | 'queryFn'
>;

/**
 * `useAssistantModelPreference()` — reads the user's saved preference
 * (`{ model: string | null }`). Cached under
 * `queryKeys.assistantModelPreference()`.
 */
export function useAssistantModelPreference(
  options?: UseAssistantModelPreferenceOptions,
): UseQueryResult<AssistantModelPreference, Error> {
  return useQuery<
    AssistantModelPreference,
    Error,
    AssistantModelPreference,
    ReturnType<typeof queryKeys.assistantModelPreference>
  >({
    queryKey: queryKeys.assistantModelPreference(),
    queryFn: () => apiService.getAssistantModelPreference(),
    ...options,
  });
}

/**
 * `useSetAssistantModelPreference()` — `PUT /api/v1/assistant/model-preference`.
 *
 * Pessimistic. On success invalidates `queryKeys.assistantModelPreference()`
 * so the header picker re-reads the persisted choice. On a 400 (model not
 * allow-listed) the rejection surfaces via `mutation.error` and nothing is
 * invalidated.
 */
export function useSetAssistantModelPreference(): UseMutationResult<
  AssistantModelPreference,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation<AssistantModelPreference, Error, string>({
    mutationFn: (model: string) =>
      apiService.setAssistantModelPreference(model),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.assistantModelPreference(),
      });
    },
  });
}

export default useAssistantModels;
